<template>
    <Drawer id="main-menu" position="left" class="main-side-menu">
        <div>
            <div class="d-flex align-items-center justify-content-between border-bottom p-16">
                <span class="text-tall-medium">{{ merchant.businessName }}</span>
                <ButtonIcon icon="far fa-times" variant="borderless" ariaLabel="Close" data-drawer="main-menu"/>
            </div>

            <div
                v-for="(textBlock, key) in storeDetails"
                :key="key"
                class="px-16 pt-8">
                <Accordion
                    v-if="textBlock.title && textBlock.content && textBlock.status"
                    :id="`side-menu-accordion-${textBlock.title.replace(/\s+/g, '')}`"
                    :isOpen="true"
                    headerClass="border-bottom">

                    <template #accordion-header>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span class="text-tall-regular midnight-800" v-html="textBlock.title"/>
                    </template>

                    <template #accordion-body>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div class="text-short-regular py-16" v-html="textBlock.content"/>
                    </template>
                </Accordion>

                <Accordion
                    id="side-menu-accordion-contact-us"
                    :isOpen="true"
                    headerClass="border-bottom">

                    <template #accordion-header>
                        <span class="text-tall-regular midnight-800" v-text="'Contact Us'"/>
                    </template>

                    <template #accordion-body>
                        <div class="text-short-regular py-16 d-flex flex-column">
                            <a class="pointer" @click="getDirections">
                                <p class="d-flex align-items-center pb-8">
                                    <Icon name="fal fa-map-marker-alt" size="1x" class="selected-colour"/>
                                    <span class="text-short-regular selected-colour pl-8" v-text="displayStreetCityPostalCode(merchant.address)"/>
                                </p>
                            </a>
                            <a :href="`tel:${merchant.address.phone}`" target="_blank">
                                <p class="d-flex align-items-center pb-8">
                                    <Icon name="fal fa-phone-alt" size="1x" class="selected-colour"/>
                                    <span class="text-short-regular selected-colour pl-8" v-text="displayPhoneNumber(merchant.address) "/>
                                </p>
                            </a>
                            <a :href="`mailto:${merchant.address.email}`" target="_blank">
                                <p class="d-flex align-items-center pb-8">
                                    <Icon name="far fa-envelope" size="1x" class="selected-colour"/>
                                    <span class="text-short-regular selected-colour pl-8" v-text="merchant.address.email"/>
                                </p>
                            </a>
                        </div>
                    </template>
                </Accordion>
            </div>

        </div>

        <div class="d-flex justify-content-center flex-column mb-24">
            <div v-if="goSettings.enableLinks" class="d-flex justify-content-center gap-8">
                <div
                    v-for="value in goSettings.links.filter((link) => link.url !== '' && Object.values(SOCIAL_MEDIA_NAMES).includes(link.title))"
                    :key="value.url">
                    <a :href="formatLink(value.url)" target="_blank">
                        <ButtonIcon
                            :icon="`fab fa-${value.title.toLowerCase()}`"
                            variant="borderless"
                            :ariaLabel="value.title"/>
                    </a>
                </div>
            </div>
            <Button
                text="Merchant Policies"
                class="mx-16"
                variant="link"
                @click="toggleModalAnimation('merchant-policy-modal')"/>
            <StorePolicies :textBlocks="textBlocks"/>
        </div>

    </Drawer>
</template>

<script setup lang="ts">
import {ButtonIcon, Drawer, Button, Icon} from '@helcim/blizzard';
import {computed} from 'vue';
import {Accordion} from '../general';
import {useTextBlocks} from '../../compositions/useTextBlocks';
import {SOCIAL_MEDIA_NAMES, TEXT_BLOCK_TEXT_ID} from '../../scripts/constants';
import StorePolicies from './StorePolicies.vue';
import { toggleModalAnimation } from '../../models/utilities/ToggleModalAnimation';
import {TextBlockDetails} from '../../types';
import {useSettings, useActors, useAddress, useMap} from '../../compositions';

const { merchant } = useActors();
const { textBlocks } = useTextBlocks();
const { goSettings } = useSettings();
const { displayStreetCityPostalCode, displayPhoneNumber, getAddressLine1 } = useAddress();
const { redirectToDirections } = useMap();

const storeDetails = computed<TextBlockDetails[]>(() => {
    return textBlocks.value.reduce((result, {textId, textDetails}) => {
        return [...result, ...textId === TEXT_BLOCK_TEXT_ID.ABOUT_US ? [textDetails] : []];
    }, []);
});

const getDirections = (() => {
    const options = {
        enableHighAccuracy: true,
    };
    navigator.geolocation.getCurrentPosition(position => {
        redirectToDirections(position.coords.latitude, position.coords.longitude, getAddressLine1(merchant.value.address), merchant.value.address.businessName);
    }, () => ({}), options);
});

const formatLink = (url: string) => {
    const protocol = window.location.protocol;
    if(!url.includes(protocol) && protocol.length > 0) {
        return `${protocol}//${url}`;
    }
    return url;
};
</script>
